/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import { useEffect, useState } from 'react';

import { useUtils } from '../../core/hooks/useUtils';
import { MODAL_TYPES, useGlobalModalContext } from '../../shared/global-modal';
import {
	connectGongAction,
	connectIntercomAction,
	connectOutreachAction,
	connectZendeskAction,
	disconnectAppAction,
	getApplicationsAction,
	sendCodeOutreachAction,
} from '../../store/Applications/action';
import { IApplicationsState } from '../../store/Applications/reducer';
import {
	IGongConnectPayload,
	IIntercomConnectPayload,
	IOutreachConnectPayload,
	IZendeskConnectPayload,
} from '../../store/Applications/types';
import {
	approveInviteAction,
	getInvitesAction,
	sendInviteAction,
} from '../../store/Invites/action';
import { IInvitesState } from '../../store/Invites/reducer';

interface IOutput {
	state: HookState;
	applications: IApplicationsState;
	invites: IInvitesState;
	connectOutreach(application: any): void;
	onDisconnect(application: any): void;
	onInvite(): void;
	getInviteLink(): void;
	connectIntercom(application: any): void;
	connectGong(application: any): void;
	connectZendesk(application: any): void;
}

interface HookState {
	isCopied: boolean;
	isScrolled: boolean;
}

export default function useHook(): IOutput {
	const { showModal } = useGlobalModalContext();

	const {
		dispatch,
		store: { applications, invites },
	} = useUtils();

	const [state, setState] = useState<HookState>({
		isCopied: false,
		isScrolled: false,
	});

	useEffect(() => {
		dispatch(getApplicationsAction());
		dispatch(getInvitesAction());
		const search = window.location.search;
		const params = new URLSearchParams(search).get('code');
		if (params) {
			dispatch(sendCodeOutreachAction(params));
		}
	}, []);

	useEffect(() => {
		window.addEventListener('scroll', handleResize);
		return () => window.removeEventListener('scroll', handleResize);
	}, []);

	const handleResize = () => {
		setState(prev => ({ ...prev, isScrolled: window.pageYOffset > 0 }));
	};

	const acceptConnectOutreach = (fields: IOutreachConnectPayload) => {
		dispatch(connectOutreachAction(fields));
	};

	const acceptConnectIntercom = (fields: IIntercomConnectPayload) => {
		dispatch(connectIntercomAction(fields));
	};

	const acceptConnectGong = (fields: IGongConnectPayload) => {
		dispatch(connectGongAction(fields));
	};

	const acceptConnectZendesk = (fields: IZendeskConnectPayload) => {
		dispatch(connectZendeskAction(fields));
	};

	const connectOutreach = (application: any) => {
		showModal(MODAL_TYPES.CONNECT_CRM, {
			confirmCallback: acceptConnectOutreach,
			item: application,
		});
	};

	const acceptDisconnectApp = (appId: string) => {
		dispatch(disconnectAppAction({ appId }));
	};

	const onDisconnect = (application: any) => {
		showModal(MODAL_TYPES.DISCONNECT_CRM, {
			confirmCallback: acceptDisconnectApp,
			item: application,
		});
	};

	const connectIntercom = (application: any) => {
		showModal(MODAL_TYPES.CONNECT_CRM, {
			confirmCallback: acceptConnectIntercom,
			item: application,
		});
	};

	const connectZendesk = (application: any) => {
		showModal(MODAL_TYPES.CONNECT_CRM, {
			confirmCallback: acceptConnectZendesk,
			item: application,
		});
	};

	const connectGong = (application: any) => {
		showModal(MODAL_TYPES.CONNECT_CRM, {
			confirmCallback: acceptConnectGong,
			item: application,
		});
	};

	const onInvite = () => {
		showModal(MODAL_TYPES.INVITE_MODAL, {
			title: 'Team Settings',
			field: 'invites',
			isMembers: true,
			inviteMember: (email: string) => sendInvite(email),
			approveMember: (email: string) => approveInvite(email),
		});
	};

	const getInviteLink = () => {
		axios.get('/users/invite').then(res => {
			navigator.clipboard.writeText(res.data.data);
			setState(prevState => ({
				...prevState,
				isCopied: true,
			}));
		});
	};

	const sendInvite = (email: string): void => {
		dispatch(
			sendInviteAction({
				email,
			})
		);
	};

	const approveInvite = (email: string): void => {
		dispatch(
			approveInviteAction({
				email,
			})
		);
	};

	return {
		state,
		applications,
		invites,
		connectOutreach,
		onDisconnect,
		onInvite,
		getInviteLink,
		connectIntercom,
		connectGong,
		connectZendesk,
	};
}
